import { useCallback, useState } from 'react';
import { batch } from 'react-redux';
import styled from 'styled-components';
import { loadAnalysisAction } from '../../../../../redux/slices/loadingAnalyses';
import { GET_ANALYSIS } from '../../../../../utils/apollo/analysis';
import {
  AnalysisStatus,
  ConversationThreadEntityType,
  ErrorType,
  IAnalysisQuery,
  IAnalysisQueryVariables,
  IDashboardAnalysisColumnFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import { useAnalysisState, useQueuedQuery } from '../../../../../utils/hooks';
import { MaybeNil } from '../../../../../utils/interfaces';
import { isNil } from 'lodash';
import { populateAnalysisThunk } from '../../../../../redux/slices/analyses';
import {
  Chart,
  ChartLoader,
  PermissionErrorChart,
} from '../../../../../components/Chart';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { useAnalytics } from '../../../../../utils/analytics/useAnalytics';
import { reportViewed } from '../../../../../utils/analytics/events/report/events';
import { ChartMode } from '../../../../../components/Chart/types';
import { DndGridDragHandle } from '../../../../../components/DndGrid';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../../../../components/ActionsVisibilityContext';
import {
  EntityConversations,
  NewConversationOverlay,
} from '../../../../../components/Conversations';
import { DASHBOARD_REPORT_COMMENT_OVERLAY_Z_INDEX } from '../../../utils/constants';
import { selectEntityConversationThreads } from '../../../../../redux/selectors/conversationThreads';
import useTrackDashboardCommentCreate from '../../../utils/useTrackDashboardCommentCreate';
import { IS_SCREENSHOT_MODE } from '../../../../../config/constants';

enum PermissionErrorType {
  Unset = 'UNSET',
  Permitted = 'PERMITTED',
  NonPermitted = 'NON_PERMITTED',
}

interface IProps {
  analysisId: string;
  entity: IDashboardAnalysisColumnFieldsFragment['analysis'];
  shouldLoad: boolean;
  onDelete: () => void;
  dragId: string;
  dragRowIndex: number;
  dragColIndex: number;
  isEmbedded?: boolean;
}

const mode = ChartMode.View;

const pollingStatuses = [AnalysisStatus.Pending, AnalysisStatus.Running];
const shouldPoll = (data: MaybeNil<IAnalysisQuery>) => {
  const status = data?.analysis?.status;
  return isNil(status) ? false : pollingStatuses.includes(status);
};

const PositionedEntityConversationThreads = styled(EntityConversations)`
  position: absolute;
  top: -12px;
  right: -12px;
`;

const DashboardReport = ({
  analysisId,
  entity,
  shouldLoad,
  onDelete,
  dragId,
  dragRowIndex,
  dragColIndex,
  isEmbedded = false,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  const { showActions, hideActions } = useActionsVisibilityContext();
  const trackNewComment = useTrackDashboardCommentCreate('dashboard report');

  const conversationThreads = useAppSelector((state) =>
    selectEntityConversationThreads(
      state,
      ConversationThreadEntityType.Analysis,
      analysisId,
    ),
  );
  const isCommentMode = useAppSelector(
    (state) => state.conversationThreads.isCommentMode,
  );

  const {
    isAnalysisLoaded,
    isAnalysisLoading,
    isAnalysisRefreshed,
    handleLoadAnalysisInStore,
  } = useAnalysisState(analysisId);

  // https://kubitai.atlassian.net/browse/KB-7103
  // Permission error state is using for handling permission error on query
  // unset means query is still loading/not fired.
  // this can prevent error when switch from nonPermitted analysis Dashboard
  // to permitted analysis Dashboard
  const [isPermissionError, setIsPermissionError] =
    useState<PermissionErrorType>(
      // KB-7518: if analysis is already loaded, we can assume it's permitted,
      // it happens after canceling dashboard edit.
      isAnalysisLoaded
        ? PermissionErrorType.Permitted
        : PermissionErrorType.Unset,
    );

  const loadAnalysisInStore = (data: MaybeNil<IAnalysisQuery>) => {
    const analysis = data?.analysis;
    setIsPermissionError(PermissionErrorType.Permitted);
    if (!isNil(analysis)) {
      batch(() => {
        dispatch(
          populateAnalysisThunk({
            analysis,
          }),
        );
        dispatch(loadAnalysisAction(analysis.id));
      });
      track(reportViewed(analysis));
    }
  };

  useQueuedQuery<IAnalysisQuery, IAnalysisQueryVariables>(GET_ANALYSIS, {
    shouldLoad,
    variables: {
      analysisId,
      refresh: !!isAnalysisRefreshed,
    },
    onError: (error) => {
      for (const graphQLError of error.graphQLErrors) {
        if (
          graphQLError.extensions?.classification ===
          ErrorType.MissingPermission
        ) {
          setIsPermissionError(PermissionErrorType.NonPermitted);
          dispatch(loadAnalysisAction(analysisId));
        }
      }
    },
    onCompleted: loadAnalysisInStore,
    shouldPoll,
  });

  /* Rebuild current chart */
  const rebuildChart = useCallback(() => {
    handleLoadAnalysisInStore(true);
  }, [handleLoadAnalysisInStore]);

  if (isAnalysisLoading || isPermissionError === PermissionErrorType.Unset) {
    return <ChartLoader display={entity.display} mode={mode} />;
  }

  if (isPermissionError === PermissionErrorType.NonPermitted) {
    return <PermissionErrorChart mode={mode} />;
  }

  return (
    <>
      <Chart
        analysisId={analysisId}
        mode={mode}
        optionsEnabled={true}
        onDelete={onDelete}
        inDashboard={true}
        rebuildChart={rebuildChart}
        dragHandle={
          <DndGridDragHandle
            id={dragId}
            rowIndex={dragRowIndex}
            colIndex={dragColIndex}
          />
        }
        onMouseEnter={showActions}
        onMouseLeave={hideActions}
        isEmbedded={isEmbedded}
      />
      {isCommentMode && (
        <NewConversationOverlay
          entityId={analysisId}
          entityType={ConversationThreadEntityType.Analysis}
          zIndex={DASHBOARD_REPORT_COMMENT_OVERLAY_Z_INDEX}
          onSubmit={trackNewComment}
        />
      )}
      {!IS_SCREENSHOT_MODE && conversationThreads.length > 0 && (
        <PositionedEntityConversationThreads
          entityId={analysisId}
          entityType={ConversationThreadEntityType.Analysis}
          threads={conversationThreads}
          placement="bottom-end"
          onAddNewComment={trackNewComment}
        />
      )}
    </>
  );
};

export default withActionsVisibilityContext(DashboardReport);
